import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";

const FACTS = [
  { k: "Legal entity", v: "IVORY PULSE SRL" },
  { k: "Registration (CUI)", v: "52619680" },
  { k: "Headquarters", v: "Romania" },
  { k: "Focus", v: "Standalone wearable AI" },
];

const LEADERSHIP = [
  { name: "Morega Darius Ștefan", role: "Founder & CEO" },
  { name: "Eduard Plai", role: "Head of Sales" },
];

const CONTACTS = [
  { label: "General", email: "contact@ivory-pulse.com" },
  { label: "Support", email: "support@ivory-pulse.com" },
  { label: "Media", email: "media@ivory-pulse.com" },
  { label: "Investors", email: "investors@ivory-pulse.com" },
  { label: "Business", email: "sales@ivory-pulse.com" },
];

const LEGAL_NAV = [
  { to: "/contact", label: "Contact" },
  { to: "/terms", label: "Terms" },
  { to: "/privacy", label: "Privacy" },
  { to: "/cookies", label: "Cookies" },
  { to: "/refunds", label: "Refunds" },
  { to: "/shipping", label: "Shipping" },
];

const Kicker = ({ children }) => (
  <div className="flex items-center gap-3">
    <span className="h-px w-8 bg-[#C7A962]" />
    <span className="font-mono text-[10.5px] uppercase tracking-[0.26em] text-[#C7A962]">
      {children}
    </span>
  </div>
);

export default function Company() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Company · Ivory Pulse";
    window.scrollTo({ top: 0, behavior: "instant" });
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="page-company">
      {/* top bar */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050505]/75 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-6 md:px-10">
          <Link
            to="/"
            data-testid="company-back-home"
            className="flex items-center gap-3 text-sm text-white/70 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="Ivory Pulse"
              draggable={false}
              className="brand-invert h-7 w-auto"
            />
          </Link>
          <Link
            to="/"
            className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/45 transition-colors hover:text-white"
          >
            Back to Ivory Pulse
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 pt-16 pb-24 md:px-10 md:pt-24 md:pb-32">
        {/* hero */}
        <Kicker>Company</Kicker>
        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 0.84, 0.3, 1] }}
          className="font-display mt-6 max-w-3xl text-4xl font-medium leading-[1.06] tracking-[-0.03em] md:text-[3.4rem]"
          data-testid="company-headline"
        >
          Intelligence that disappears
          <br />
          <span className="text-white/35">into everyday life.</span>
        </motion.h1>
        <p className="mt-7 max-w-2xl text-[16px] leading-[1.75] text-white/60 md:text-lg">
          Ivory Pulse is a Romania-based technology company building Pulse One
          — a standalone AI companion worn on the wrist, designed to make
          intelligence present without adding another screen.
        </p>

        {/* facts strip */}
        <div
          className="mt-12 grid grid-cols-2 gap-6 border-t border-white/10 pt-7 md:grid-cols-4"
          data-testid="company-facts"
        >
          {FACTS.map((f) => (
            <div key={f.k} className="flex flex-col gap-1">
              <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-white/35">{f.k}</span>
              <span className="text-[14px] font-medium tracking-tight text-white/85">{f.v}</span>
            </div>
          ))}
        </div>

        {/* mission */}
        <section className="mt-20 md:mt-28" data-testid="company-mission">
          <Kicker>Mission</Kicker>
          <p className="font-display mt-7 max-w-3xl text-2xl font-medium leading-[1.35] tracking-[-0.02em] text-white/90 md:text-[2rem]">
            Build the world's most natural AI companion — one that is always
            available, seamlessly integrated into everyday life, and designed
            to make human intelligence more accessible without adding another
            screen.
          </p>
        </section>

        {/* why */}
        <section className="mt-20 md:mt-28" data-testid="company-why">
          <Kicker>Why we are building Pulse One</Kicker>
          <div className="mt-8 grid gap-10 md:grid-cols-2 md:gap-14">
            <p className="text-[15px] leading-[1.85] text-white/65 md:text-base">
              Every era of computing has pulled people toward a screen.
              Phones made intelligence portable, but they also made it
              demanding — a quick question becomes an unlocked screen, an
              open app, and ten minutes of attention that was never part of
              the plan.
            </p>
            <p className="text-[15px] leading-[1.85] text-white/65 md:text-base">
              Pulse One takes the opposite path: a wearable with its own
              connection to the world, built around voice-first AI and a
              privacy-first design. It answers when asked, remembers what you
              choose, and stays silent otherwise. We reveal hardware once,
              properly — and we announce milestones when they are certain.
            </p>
          </div>
        </section>

        {/* founder quote */}
        <section className="mt-20 md:mt-28" data-testid="company-founder-quote">
          <figure className="relative overflow-hidden rounded-none border border-white/10 bg-[#0A0A0A] p-8 md:p-14">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{ background: "radial-gradient(50% 60% at 12% 0%, rgba(199,169,98,0.10) 0%, transparent 60%)" }}
            />
            <div className="rule-gold mb-8 w-24" />
            <blockquote className="font-display relative max-w-3xl text-xl font-medium leading-[1.5] tracking-[-0.015em] text-white/90 md:text-2xl">
              "We believe the next era of computing won't be defined by
              another screen — it will be defined by intelligence that is
              always present, always helpful, and disappears into everyday
              life. Our mission is to build that future."
            </blockquote>
            <figcaption className="relative mt-7">
              <span className="block text-[15px] font-medium tracking-tight text-white">
                Morega Darius Ștefan
              </span>
              <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                Founder & CEO
              </span>
            </figcaption>
          </figure>
        </section>

        {/* leadership */}
        <section className="mt-20 md:mt-28" data-testid="company-leadership">
          <Kicker>Leadership</Kicker>
          <div className="mt-8 flex flex-col">
            {LEADERSHIP.map((m, i) => (
              <div
                key={m.name}
                data-testid={`leadership-member-${i}`}
                className="flex flex-col gap-1 border-t border-white/10 py-7 last:border-b sm:flex-row sm:items-baseline sm:justify-between"
              >
                <span className="font-display text-xl font-medium tracking-tight text-white md:text-2xl">
                  {m.name}
                </span>
                <span className="font-mono text-[10.5px] uppercase tracking-[0.2em] text-[#E8D9B0]">
                  {m.role}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* corporate contacts */}
        <section className="mt-20 md:mt-28" data-testid="company-contacts">
          <Kicker>Corporate contact</Kicker>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {CONTACTS.map((c) => (
              <a
                key={c.email}
                href={`mailto:${c.email}`}
                data-testid={`company-contact-${c.label.toLowerCase()}`}
                className="group flex items-center justify-between rounded-none border border-white/10 bg-[#0A0A0A] px-5 py-4 transition-colors duration-300 hover:border-[#C7A962]/40"
              >
                <span>
                  <span className="block font-mono text-[9.5px] uppercase tracking-[0.2em] text-white/40 transition-colors group-hover:text-[#C7A962]">
                    {c.label}
                  </span>
                  <span className="mt-0.5 block text-[13.5px] text-white/80">{c.email}</span>
                </span>
                <ArrowUpRight className="h-4 w-4 text-white/30 transition-colors group-hover:text-[#C7A962]" />
              </a>
            ))}
          </div>
          <p className="mt-6 text-[13px] leading-relaxed text-white/40">
            IVORY PULSE SRL · Registration (CUI) 52619680 · Registered office
            in Romania. Pulse One is a wearable AI companion, not a medical
            device.
          </p>
        </section>
      </main>

      {/* footer strip */}
      <footer className="border-t border-white/10">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-6 px-6 py-10 md:flex-row md:px-10">
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-white/55">
            {LEGAL_NAV.map((l) => (
              <Link key={l.to} to={l.to} className="transition-colors hover:text-white">
                {l.label}
              </Link>
            ))}
          </nav>
          <p className="text-center text-[11px] text-white/40 md:text-right">
            © {new Date().getFullYear()} IVORY PULSE SRL. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
